import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileSaver from "file-saver";
import { arrType, getColDefaultValue } from "data/globalData";
import RenderInputField from "components/EboStoreV4/CommonComponents/RenderInputFiled/RenderInputFiled";
import { SbtRoleAccess, storeBudgetTrackerFields } from "data/storeBudgetData";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { AuthState } from "context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import BudgetAllocationTable, {
  buildSelectionColumn,
  getApVoucherColumns,
  getApVoucherMatchKey,
  getApVoucherSearchValues,
  getServiceOrderColumns,
  getServiceOrderMatchKey,
  getServiceOrderSearchValues,
  mapApVoucherRows,
  mapServiceOrderRows,
  normalizeApVoucherForPayload,
  normalizeServiceOrderForPayload,
  useDocAllocationTable,
} from "./BudgetAllocationTable";

const formatCurrency = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;
const formatDate = (value) => {
  if (!value) return "";
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return String(value).split("T")[0];
};
const isTaggedRow = (row) => Boolean(row?.tagged);
const getTaggedProjectId = (row, fallback = "-") => row?.tagged || fallback;
const isGrnCompleted = (row) => String(row?.item_status || "").toLowerCase().includes("complete");
const emptyRowEvidence = () => ({ actualBarcode: "", item_remark: "", item_comment: "", pictures: [] });
const getRowActualBarcodeValue = (row, evidence = emptyRowEvidence()) =>
  (evidence?.actualBarcode || row?.actual_barcode || "").trim();
const MAX_GRN_PICTURE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_GRN_PICTURE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ItemDetailsSummary = ({ row }) => {
  const status = row?.item_status || "-";
  const statusColor = String(status).toLowerCase().includes("complete") ? "success" : "default";

  const items = [
    { label: "Material", value: row?.material || row?.section },
    { label: "Vendor", value: row?.vendor_name },
    { label: "PO", value: row?.po_number },
    { label: "Barcode", value: row?.barcode },
    { label: "GRN", value: row?.grn_no },
    { label: "Invoice value", value: formatCurrency(row?.invoice_value_with_tax) },
  ];

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        border: "1px solid #dbeafe",
        backgroundColor: "#f8fbff",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} marginBottom={1}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Item reference
        </Typography>
        <Chip size="small" color={statusColor} label={status} />
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
          gap: 1.25,
        }}
      >
        {items.map((item) => (
          <Box key={item.label} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
              {item.label}
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
              {item.value || "-"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const GrnPictureThumbnail = ({ picture, onClick, onRemove, showRemove }) => {
  const [src, setSrc] = useState(picture?.preview_url || picture?.previewUrl || "");
  const [loading, setLoading] = useState(
    !picture?.preview_url && !picture?.previewUrl && Boolean(picture?.gcs_path)
  );

  useEffect(() => {
    let active = true;
    let objectUrl = "";

    const existingPreview = picture?.preview_url || picture?.previewUrl;
    if (existingPreview) {
      setSrc(existingPreview);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    if (picture?.localFile) {
      objectUrl = URL.createObjectURL(picture.localFile);
      setSrc(objectUrl);
      setLoading(false);
      return () => {
        active = false;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    if (!picture?.gcs_path) {
      setSrc("");
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    pythonVmInstance({
      url: `/store-budget-tracker/preview-po-picture`,
      params: {
        gcs_path: picture.gcs_path,
        file_name: picture.file_name,
      },
      method: "GET",
    })
      .then((res) => {
        if (active && res?.data?.url) {
          setSrc(res.data.url);
        }
      })
      .catch(() => {
        if (active) setSrc("");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [picture]);

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "1px solid #e5e7eb",
        aspectRatio: "1 / 1",
        backgroundColor: "#f8fafc",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {loading ? (
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      ) : src ? (
        <Box
          component="img"
          src={src}
          alt={picture?.file_name || "Item photo"}
          onClick={onClick}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            cursor: "pointer",
          }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ px: 1, textAlign: "center" }}>
          Preview unavailable
        </Typography>
      )}
      {showRemove ? (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            backgroundColor: "rgba(255,255,255,0.92)",
          }}
        >
          <DeleteOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
};

const getPoRowKey = (row) =>
  `${row?.barcode || row?.BARCODE || ""}|${row?.po_number || row?.PO_NO || ""}|${row?.grn_no || row?.GRN_NO || ""}|${row?.invoice_no || row?.INVOICE_NO || ""}`;

const getDateAtStartOfDay = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

const DateRangeFilterInput = ({ item, applyValue, focusElementRef }) => {
  const [fromValue = "", toValue = ""] = Array.isArray(item.value) ? item.value : [];

  const handleChange = (index, value) => {
    const nextValue = [fromValue, toValue];
    nextValue[index] = value;
    applyValue({ ...item, value: nextValue });
  };

  return (
    <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
      <TextField
        inputRef={focusElementRef}
        label="From"
        type="date"
        variant="standard"
        value={fromValue}
        onChange={(event) => handleChange(0, event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="To"
        type="date"
        variant="standard"
        value={toValue}
        onChange={(event) => handleChange(1, event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </Stack>
  );
};

const dateBetweenFilterOperator = {
  label: "Between",
  value: "between",
  getApplyFilterFn: (filterItem) => {
    if (!Array.isArray(filterItem.value)) {
      return null;
    }

    const [fromValue, toValue] = filterItem.value;
    const fromDate = getDateAtStartOfDay(fromValue);
    const toDate = getDateAtStartOfDay(toValue);

    if (!fromDate && !toDate) {
      return null;
    }

    return (value) => {
      const rowDate = getDateAtStartOfDay(value);
      if (!rowDate) return false;
      if (fromDate && rowDate < fromDate) return false;
      if (toDate && rowDate > toDate) return false;
      return true;
    };
  },
  InputComponent: DateRangeFilterInput,
};

const getRowsAfterGridFilters = (rows, filterModel) => {
  const filterItems = filterModel?.items?.filter((item) => item?.field === "po_date" && item?.operator === "between") || [];
  if (!filterItems.length) return rows;

  const logicOperator = filterModel?.logicOperator || filterModel?.linkOperator || "and";

  return rows.filter((row) => {
    const results = filterItems.map((item) => {
      const filterFn = dateBetweenFilterOperator.getApplyFilterFn(item);
      return filterFn ? filterFn(row?.po_date) : true;
    });

    return logicOperator === "or" ? results.some(Boolean) : results.every(Boolean);
  });
};

const isActiveBudgetPdf = (pdf) => String(pdf?.status || "").toLowerCase() === "active";
const MAX_BUDGET_PDF_BYTES = 20 * 1024 * 1024;

const normalizeNullableDate = (value) => {
  if (value === "" || value === "-") return null;
  return value ?? null;
};

const PO_PAYLOAD_KEYS = [
  "section",
  "material",
  "barcode",
  "vendor_name",
  "store_name",
  "site_code",
  "po_date",
  "po_number",
  "po_remark",
  "item_status",
  "po_tax",
  "po_value",
  "po_item_value",
  "po_item_with_tax",
  "invoice_no",
  "grn_no",
  "grn_date",
  "invoice_value_with_tax",
  "invoice_value_without_tax",
  "invoice_date",
  "actual_barcode",
  "item_remark",
  "item_comment",
  "grn_pictures",
];

const normalizePoDetailForPayload = (row, evidence) => {
  const payload = {};
  PO_PAYLOAD_KEYS.forEach((key) => {
    const value = row?.[key];
    if (key === "item_remark") {
      payload[key] = evidence?.item_remark?.trim() || row?.item_remark || null;
      return;
    }
    if (key === "item_comment") {
      payload[key] = evidence?.item_comment?.trim() || row?.item_comment || null;
      return;
    }
    if (key === "actual_barcode") {
      payload[key] = evidence?.actualBarcode?.trim() || row?.actual_barcode || null;
      return;
    }
    if (key === "grn_pictures") {
      const pictures = evidence?.pictures || row?.grn_pictures || [];
      payload[key] = pictures
        .filter((picture) => picture?.gcs_path)
        .map((picture) => ({
          file_name: picture.file_name,
          gcs_path: picture.gcs_path,
          uploaded_by: picture.uploaded_by,
          uploaded_at: picture.uploaded_at,
        }));
      return;
    }
    payload[key] = key.endsWith("_date") ? normalizeNullableDate(value) : value ?? null;
  });
  return payload;
};

const mapApiRowToBudgetRow = (row, index) => {
  const poValue = Number(row?.PO_WITHOUT_TAX) || 0;
  const poWithTax = Number(row?.PO_WITH_TAX) || 0;
  const grossAmount = Number(row?.GROSS_AMT) || 0;
  const netAmount = Number(row?.NET_AMT) || 0;
  const poItemValue = Number(row?.PO_ITEM_WITHOUT_TAX) || 0;
  const poItemWithTax = Number(row?.PO_ITEM_WITH_TAX) || 0;

  return {
    id: `${row?.PO_NO || "PO"}-${row?.GRN_NO || "GRN"}-${row?.INVOICE_NO || "INV"}-${row?.BARCODE || row?.barcode || "BC"}${index ? index : ""}`,
    material: row?.DEPARTMENT,
    section: row?.SECTION,
    vendor_name: row?.VENDOR_NAME,
    po_number: row?.PO_NO,
    // po_status: row?.STATUS,
    po_remark: row?.REMARK,
    tagged: row?.TAGGED ?? row?.tagged ?? row?.tagged_key ?? row?.taggedKey ?? "",
    project_id: row?.PROJECT_ID ?? row?.project_id ?? row?.PROJECTID ?? row?.PROJECTID_NO,
    po_date: formatDate(row?.PO_DATE),
    po_value: poValue,
    po_item_value: poItemValue,
    po_item_with_tax: poItemWithTax,
    po_tax: Math.max(poWithTax - poValue, 0),
    // invoice_receive_date: formatDate(row?.INVOICE_DATE),
    invoice_no: row?.INVOICE_NO,
    invoice_date: formatDate(row?.INVOICE_DATE),
    // delay_in_invoice: "-",
    invoice_value_without_tax: grossAmount,
    invoice_value_with_tax: netAmount,
    // grn_request_date: "-",
    grn_date: formatDate(row?.GRNDATE),
    grn_no: row?.GRN_NO,
    // grn_tat: "-",
    store_name: row?.SITE_NAME,
    // total_budget: Number(row?.total_budget) || 0,
    site_code: row?.SITE_CODE,
    // remark: row?.REMARK,
    item_status: row?.GRN_STATUS,
    barcode: row?.BARCODE,
    actual_barcode: row?.actual_barcode || "",
    item_remark: row?.item_remark || "",
    item_comment: row?.item_comment || "",
    grn_pictures: row?.grn_pictures || [],
  };
};

const mapApiRows = (rows = []) => {
  const keyCount = new Map();
  return rows.map((row) => {
    const baseKey = `${row?.PO_NO || "PO"}-${row?.GRN_NO || "GRN"}-${row?.INVOICE_NO || "INV"}-${row?.BARCODE || row?.barcode || "BC"}`;
    const count = keyCount.get(baseKey) || 0;
    keyCount.set(baseKey, count + 1);
    return mapApiRowToBudgetRow(row, count === 0 ? "" : `-${count}`);
  });
};

const getDataColumns = (projectTag) => [
  { field: "section", headerName: "Section", minWidth: 220, flex: 1.3 },
  { field: "material", headerName: "Materials", minWidth: 220, flex: 1.3 },
  { field: "barcode", headerName: "Barcode", minWidth: 120, flex: 0.9 },
  { field: "store_name", headerName: "Location (Store Name)", minWidth: 190, flex: 1.3 },
  { field: "vendor_name", headerName: "Vendor Name", minWidth: 170, flex: 1.2 },
  { field: "po_number", headerName: "PO No.", minWidth: 120, flex: 0.9 },
  {
    field: "po_date",
    headerName: "PO Date",
    minWidth: 120,
    flex: 0.9,
    type: "date",
    valueGetter: (value) => getDateAtStartOfDay(value),
    valueFormatter: (value) => formatDate(value),
    filterOperators: [dateBetweenFilterOperator],
  },
  {
    field: "po_item_value",
    headerName: "PO item without tax",
    minWidth: 150,
    flex: 1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.po_item_value),
  },
  {
    field: "po_item_with_tax",
    headerName: "PO item with tax",
    minWidth: 150,
    flex: 1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.po_item_with_tax),
  },
  {
    field: "po_value",
    headerName: "PO amt without tax",
    minWidth: 150,
    flex: 1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.po_value),
  },
  {
    field: "po_amt_with_tax",
    headerName: "PO amt with Tax",
    minWidth: 145,
    flex: 1,
    type: "number",
    valueGetter: (value, row) => (row?.po_value || 0) + (row?.po_tax || 0),
    renderCell: ({ row }) => formatCurrency((row?.po_value || 0) + (row?.po_tax || 0)),
  },
  // { field: "po_status", headerName: "PO STATUS", minWidth: 120, flex: 0.9 },
  // { field: "invoice_receive_date", headerName: "Invoice Recieve Date", minWidth: 165, flex: 1.1 },
  { field: "invoice_no", headerName: "Invoice No", minWidth: 125, flex: 0.9 },
  { field: "invoice_date", headerName: "Invoice date", minWidth: 125, flex: 0.9 },
  // { field: "delay_in_invoice", headerName: "Dealy in Invoice", minWidth: 130, flex: 0.9 },
  {
    field: "invoice_value_without_tax",
    headerName: "Invoice amt without tax",
    minWidth: 170,
    flex: 1.1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.invoice_value_without_tax),
  },
  {
    field: "invoice_value_with_tax",
    headerName: "Invoice amt with Tax",
    minWidth: 160,
    flex: 1,
    type: "number",
    renderCell: ({ row }) => formatCurrency(row?.invoice_value_with_tax),
  },
  // { field: "grn_request_date", headerName: "GRN Request Date", minWidth: 155, flex: 1 },
  { field: "grn_no", headerName: "GRN No", minWidth: 120, flex: 0.9 },
  { field: "grn_date", headerName: "GRN Date", minWidth: 120, flex: 0.9 },
  { field: "item_status", headerName: "Status", minWidth: 100, flex: 0.8 },
].map((column) => ({
  headerAlign: "center",
  align: "center",
  sortable: true,
  ...column,
}));

const poDataGridSx = {
  height: "100%",
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#dbeafe",
    fontWeight: 600,
    borderRight: "1px solid #bfdbfe",
  },
  "& .MuiDataGrid-columnHeader[data-field='__selectionSort'] .MuiDataGrid-iconButtonContainer": {
    visibility: "visible",
    width: "auto",
  },
  "& .MuiDataGrid-columnHeader[data-field='__selectionSort'] .MuiDataGrid-sortIcon": {
    opacity: 1,
  },
  "& .MuiDataGrid-cell": {
    borderColor: "#eceff1",
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: "1px solid #eceff1",
  },
};

const pdfDataGridSx = {
  border: "none",
  height: "auto",
  maxHeight: 320,
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#dbeafe",
    fontWeight: 600,
    borderRight: "1px solid #bfdbfe",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  "& .MuiDataGrid-cell": {
    borderColor: "#eceff1",
    display: "flex",
    alignItems: "center",
    py: 0.5,
  },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "#f8fbff" },
  "& .MuiDataGrid-footerContainer": { borderTop: "1px solid #eceff1", minHeight: 44 },
  "& .MuiDataGrid-virtualScroller": {
    minHeight: 64,
    maxHeight: 220,
  },
  "& .MuiDataGrid-overlay": { minHeight: 64 },
};

const comparisonCardSx = {
  flex: 1,
  minWidth: 180,
  p: 2,
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fafafa",
};

const invoicePanelSx = {
  flex: 1,
  minWidth: 320,
  borderRadius: 3,
  overflow: "hidden",
  border: "1px solid #dbe2ef",
  backgroundColor: "#ffffff",
};

const invoicePanelHeaderSx = (backgroundColor) => ({
  px: 2,
  py: 1.5,
  backgroundColor,
  color: "#fff",
});

const invoiceRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  px: 2,
  py: 1.5,
  borderBottom: "1px solid #eef2f7",
};

const initial_state = () => {
    const obj = {};

    storeBudgetTrackerFields?.forEach((attr) => {
      obj[attr.key] = getColDefaultValue(attr);
    });
    return obj;
  };

export default function CreateStoreBudget() {
  const location = useLocation();
  const storeBudgetData = location.state?.data;
  const isSubmitted = storeBudgetData?.status === "submitted";
  const [storeBudget, setStoreBudget] = useState(storeBudgetData === undefined ? initial_state() : storeBudgetData);
  const [poNumberFilter, setPoNumberFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [storeData, setStoreData] = useState([]);
  const [poData, setPoData] = useState([]);
  const [isSelectedPoDialogOpen, setIsSelectedPoDialogOpen] = useState(false);
  const [savedRows, setSavedRows] = useState([]);
  const [isPoTableLoading, setIsPoTableLoading] = useState(false);
  const [soNumberFilter, setSoNumberFilter] = useState("");
  const [soTableSearch, setSoTableSearch] = useState("");
  const [soData, setSoData] = useState([]);
  const [savedSoRows, setSavedSoRows] = useState([]);
  const [isSoTableLoading, setIsSoTableLoading] = useState(false);
  const [apVoucherFilter, setApVoucherFilter] = useState("");
  const [apTableSearch, setApTableSearch] = useState("");
  const [apData, setApData] = useState([]);
  const [savedApRows, setSavedApRows] = useState([]);
  const [isApTableLoading, setIsApTableLoading] = useState(false);
  const [gridFilterModel, setGridFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  // Cycles: selected first → unselected first → tagged first → clear
  const [selectionSortMode, setSelectionSortMode] = useState(null);
  const [budgetPdfs, setBudgetPdfs] = useState(storeBudgetData?.pdfs || []);
  const [pendingPdfFile, setPendingPdfFile] = useState(null);
  const [isRejectPdfDialogOpen, setIsRejectPdfDialogOpen] = useState(false);
  const [rejectPdfReason, setRejectPdfReason] = useState("");
  const [rowEvidenceMap, setRowEvidenceMap] = useState({});
  const [grnEvidenceDialog, setGrnEvidenceDialog] = useState({ open: false, row: null });
  const [grnEvidenceDraft, setGrnEvidenceDraft] = useState(emptyRowEvidence());
  const [actualBarcodeError, setActualBarcodeError] = useState(false);
  const [isSavingItemDetails, setIsSavingItemDetails] = useState(false);
  const pdfInputRef = useRef(null);
  const grnPictureInputRef = useRef(null);
  const selectedRowIdSetRef = useRef(new Set());
  const rowEvidenceMapRef = useRef({});
  const visibleSelectableRowIdsRef = useRef([]);
  const openGrnEvidenceDialogRef = useRef(() => {});
  const isBudgetAvailableRef = useRef(false);
  const isSavingBudgetRef = useRef(false);

  const deferredPoNumberFilter = useDeferredValue(poNumberFilter);
  const deferredTableSearch = useDeferredValue(tableSearch);
  const deferredSoNumberFilter = useDeferredValue(soNumberFilter);
  const deferredSoTableSearch = useDeferredValue(soTableSearch);
  const deferredApVoucherFilter = useDeferredValue(apVoucherFilter);
  const deferredApTableSearch = useDeferredValue(apTableSearch);
  const selectedRowIdSet = useMemo(() => new Set(selectedRows), [selectedRows]);
  selectedRowIdSetRef.current = selectedRowIdSet;
  rowEvidenceMapRef.current = rowEvidenceMap;

  const serviceOrderTable = useDocAllocationTable({
    isSubmitted,
    savedRows: savedSoRows,
    apiData: soData,
    mapRows: mapServiceOrderRows,
    getMatchKey: getServiceOrderMatchKey,
    numberField: "order_no",
    searchFilter: deferredSoNumberFilter,
    tableSearch: deferredSoTableSearch,
    searchValueGetter: getServiceOrderSearchValues,
  });
  const apVoucherTable = useDocAllocationTable({
    isSubmitted,
    savedRows: savedApRows,
    apiData: apData,
    mapRows: mapApVoucherRows,
    getMatchKey: getApVoucherMatchKey,
    numberField: "voucher_number",
    searchFilter: deferredApVoucherFilter,
    tableSearch: deferredApTableSearch,
    searchValueGetter: getApVoucherSearchValues,
  });
  const { user } = AuthState();
  const roleData = SbtRoleAccess(user)
  const isViewOnly = roleData?.roles === "VIEWER"

  const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
  const navigate = useNavigate();

  const handleBackClick = () => {

			navigate(`/explore/store_budget_tracker/store_budgets`)
    	}

  const getRowEvidence = (row) => rowEvidenceMapRef.current[getPoRowKey(row)] || emptyRowEvidence();

  const updateRowEvidence = (row, patch) => {
    const rowKey = getPoRowKey(row);
    setRowEvidenceMap((prev) => ({
      ...prev,
      [rowKey]: {
        ...emptyRowEvidence(),
        ...prev[rowKey],
        ...patch,
      },
    }));
  };

  const openGrnEvidenceDialog = (row) => {
    if (!row || !isGrnCompleted(row)) return;
    if (isTaggedRow(row) && !isSubmitted && !isViewOnly) return;
    if (!isSubmitted && !isViewOnly && !selectedRowIdSetRef.current.has(row.id)) return;
    const evidence = rowEvidenceMapRef.current[getPoRowKey(row)];
    setGrnEvidenceDraft({
      actualBarcode: evidence?.actualBarcode || row?.actual_barcode || "",
      item_remark: evidence?.item_remark || row?.item_remark || "",
      item_comment: evidence?.item_comment || row?.item_comment || "",
      pictures: [...(evidence?.pictures ?? row?.grn_pictures ?? [])],
    });
    setActualBarcodeError(false);
    setGrnEvidenceDialog({ open: true, row });
  };
  openGrnEvidenceDialogRef.current = openGrnEvidenceDialog;

  const closeGrnEvidenceDialog = () => {
    setGrnEvidenceDialog({ open: false, row: null });
    setGrnEvidenceDraft(emptyRowEvidence());
    setActualBarcodeError(false);
  };

  const persistStoreBudgetDraft = async (rowKey, evidencePatch, includeRow = null) => {
    const rowsToPersist =
      includeRow && !selectedPoRows.some((item) => getPoRowKey(item) === getPoRowKey(includeRow))
        ? [...selectedPoRows, includeRow]
        : selectedPoRows;

    const data = {
      ...storeBudget,
      store_opening_date: storeBudget?.store_opening_date || null,
      po_details: rowsToPersist.map((row) => {
        const evidence =
          rowKey && getPoRowKey(row) === rowKey
            ? { ...getRowEvidence(row), ...evidencePatch }
            : getRowEvidence(row);
        return normalizePoDetailForPayload(row, evidence);
      }),
      so_details: serviceOrderTable.selectedDocRows.map(normalizeServiceOrderForPayload),
      ap_voucher_details: apVoucherTable.selectedDocRows.map(normalizeApVoucherForPayload),
      email: user.email,
      is_submitted: false,
    };
    delete data.pdfs;
    delete data.pos;
    delete data.service_orders;
    delete data.ap_vouchers;

    const res = await pythonVmInstance({
      url: `/store-budget-tracker/create`,
      data: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });
    const createdId = res?.data?.id || storeBudget?.id;
    if (createdId) {
      setStoreBudget((prev) => ({ ...prev, id: createdId }));
    }
    return createdId;
  };

  const uploadPoPicture = async (budgetId, row, file, { skipPreview = false } = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("store_budget_id", budgetId);
    form.append("po_number", row?.po_number || "");
    form.append("barcode", row?.barcode || "");
    form.append("grn_no", row?.grn_no || "");
    form.append("invoice_no", row?.invoice_no || "");
    form.append("email", user.email);
    if (skipPreview) form.append("skip_preview", "true");
    const res = await pythonVmInstance({
      url: `/store-budget-tracker/upload-po-picture`,
      data: form,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res?.data?.picture;
  };

  const savePoItemDetailsApi = async (budgetId, row, evidencePatch, pictures) => {
    const savedPictures = (pictures || [])
      .filter((picture) => picture?.gcs_path)
      .map((picture) => ({
        id: picture.id,
        file_name: picture.file_name,
        gcs_path: picture.gcs_path,
        uploaded_by: picture.uploaded_by,
        uploaded_at: picture.uploaded_at,
        preview_url: picture.preview_url,
      }));

    const res = await pythonVmInstance({
      url: `/store-budget-tracker/save-po-item-details`,
      data: JSON.stringify({
        store_budget_id: budgetId,
        po_number: row?.po_number || "",
        barcode: row?.barcode || "",
        grn_no: row?.grn_no || "",
        invoice_no: row?.invoice_no || "",
        actual_barcode: evidencePatch.actualBarcode,
        item_remark: evidencePatch.item_remark,
        item_comment: evidencePatch.item_comment,
        grn_pictures: savedPictures,
        po_detail: normalizePoDetailForPayload(row, evidencePatch),
        email: user.email,
      }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });
    return res?.data?.item_details?.grn_pictures || savedPictures;
  };

  const saveGrnEvidenceDraft = async () => {
    const row = grnEvidenceDialog.row;
    if (!row) return;

    const actualBarcode = grnEvidenceDraft.actualBarcode?.trim() || "";
    if (!actualBarcode) {
      setActualBarcodeError(true);
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Actual barcode is required",
        severity: "warning",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return;
    }

    const evidencePatch = {
      actualBarcode,
      item_remark: grnEvidenceDraft.item_remark || "",
      item_comment: grnEvidenceDraft.item_comment || "",
      pictures: grnEvidenceDraft.pictures || [],
    };
    const rowKey = getPoRowKey(row);

    try {
      setIsSavingItemDetails(true);
      setLoadingOpen(true);

      // Same as before: persist budget + all selected PO rows first
      const budgetId = await persistStoreBudgetDraft(rowKey, evidencePatch, row);
      if (!budgetId) {
        throw new Error("Unable to save store budget");
      }

      const draftPictures = [...(grnEvidenceDraft.pictures || [])];
      const pendingUploads = draftPictures
        .map((picture, index) => ({ picture, index }))
        .filter(({ picture }) => picture?.localFile);

      // Faster only: upload pending photos in parallel (behavior unchanged)
      if (pendingUploads.length) {
        const uploadedList = await Promise.all(
          pendingUploads.map(({ picture }) =>
            uploadPoPicture(budgetId, row, picture.localFile, {
              skipPreview: true,
            }).then((uploaded) => ({
              ...uploaded,
              preview_url: uploaded?.preview_url || picture.preview_url,
            }))
          )
        );
        pendingUploads.forEach(({ index }, uploadIndex) => {
          draftPictures[index] = uploadedList[uploadIndex];
        });
      }

      // Same as before: final item-details save with full picture list
      const savedPictures = await savePoItemDetailsApi(budgetId, row, evidencePatch, draftPictures);

      updateRowEvidence(row, {
        ...evidencePatch,
        pictures: (savedPictures || [])
          .filter((picture) => picture?.gcs_path)
          .map((picture) => {
            const localMatch = draftPictures.find((item) => item?.gcs_path === picture.gcs_path);
            return {
              ...picture,
              preview_url: picture.preview_url || localMatch?.preview_url,
            };
          }),
      });

      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Item details saved successfully",
        severity: "success",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      closeGrnEvidenceDialog();
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || error?.message || "Failed to save item details",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setIsSavingItemDetails(false);
      setLoadingOpen(false);
    }
  };

  const syncPendingGrnPictures = async (budgetId, rows) => {
    const uploads = [];
    rows.forEach((row) => {
      const evidence = getRowEvidence(row);
      (evidence.pictures || []).forEach((picture) => {
        if (picture?.localFile) {
          uploads.push({ row, file: picture.localFile });
        }
      });
    });
    if (!uploads.length) return;

    const nextMap = { ...rowEvidenceMap };
    for (const item of uploads) {
      const uploaded = await uploadPoPicture(budgetId, item.row, item.file);
      const rowKey = getPoRowKey(item.row);
      const current = nextMap[rowKey] || emptyRowEvidence();
      const withoutPending = (current.pictures || []).filter((picture) => !picture?.localFile);
      nextMap[rowKey] = {
        ...current,
        pictures: [...withoutPending, uploaded],
      };
    }
    setRowEvidenceMap(nextMap);
  };

  const handleGrnPictureSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !grnEvidenceDialog.row) return;

    if (!grnEvidenceDraft.actualBarcode?.trim()) {
      setActualBarcodeError(true);
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Enter actual barcode before adding photos",
        severity: "warning",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return;
    }

    const nextPictures = [...(grnEvidenceDraft.pictures || [])];

    for (const file of files) {
      if (!ACCEPTED_GRN_PICTURE_TYPES.includes(file.type)) {
        setSnackBarOpen(true);
        setSnackBar({
          ...snackBar,
          message: "Only JPG, PNG, or WEBP images are allowed",
          severity: "warning",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
        continue;
      }
      if (file.size > MAX_GRN_PICTURE_BYTES) {
        setSnackBarOpen(true);
        setSnackBar({
          ...snackBar,
          message: "Each image should be 5 MB or less",
          severity: "warning",
          anchorOrigin: { horizontal: "center", vertical: "top" },
        });
        continue;
      }

      nextPictures.push({
        file_name: file.name,
        preview_url: URL.createObjectURL(file),
        localFile: file,
      });
    }

    setGrnEvidenceDraft((prev) => ({ ...prev, pictures: nextPictures }));
  };

  const removeGrnPicture = (index) => {
    setGrnEvidenceDraft((prev) => ({
      ...prev,
      pictures: (prev.pictures || []).filter((_, pictureIndex) => pictureIndex !== index),
    }));
  };

  const openGrnPicturePreview = async (picture) => {
    const cachedPreview = picture?.preview_url || picture?.previewUrl;
    if (cachedPreview) {
      window.open(cachedPreview, "_blank", "noopener,noreferrer");
      return;
    }
    if (picture?.localFile) {
      window.open(URL.createObjectURL(picture.localFile), "_blank", "noopener,noreferrer");
      return;
    }
    if (!picture?.gcs_path) return;
    try {
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/preview-po-picture`,
        params: { gcs_path: picture.gcs_path, file_name: picture.file_name },
        method: "GET",
      });
      const signedUrl = res?.data?.url;
      if (signedUrl) {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || "Failed to preview picture",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setLoadingOpen(false);
    }
  };
  

  const getStores = async () => {
    try {
      setLoadingOpen(true);
      await pythonVmInstance({
        url: `/store-budget-tracker/get-store`,
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      }).then((res) => {
        setStoreData(
          [...(res?.data || [])].sort((a, b) =>
            String(a?.store_name || "").localeCompare(String(b?.store_name || ""), undefined, {
              sensitivity: "base",
              numeric: true,
            })
          )
        );
        setLoadingOpen(false);
      });
    } catch (error) {
      setSnackBarOpen(true);
      setLoadingOpen(false);
      setSnackBar({
        ...snackBar,
        message: "Failed to download file",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    }
  };
  const getPO = async (poFilter = "") => {
    try {
      setIsPoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-po`,
        params: {
          store: storeBudget?.store_name,
          id : storeBudget?.id,
          ...(poFilter ? { po_number: poFilter } : {}),
        },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      setPoData((prev) => {
        const existingKeys = new Set(prev.map((r) => `${r?.PO_NO}-${r?.GRN_NO}-${r?.INVOICE_NO}`));
        const newRows = (res?.data || []).filter((r) => !existingKeys.has(`${r?.PO_NO}-${r?.GRN_NO}-${r?.INVOICE_NO}`));
        return [...prev, ...newRows];
      });
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: "Failed to download file", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
    } finally {
      setIsPoTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const getSelectedPO = async () => {
    try {
      setIsPoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-selected-po`,
        params: { id: storeBudgetData?.id },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const data = res?.data || [];
      setSavedRows(data);
      return [...new Set(data.map((r) => r?.po_number || r?.PO_NO).filter(Boolean))].join(",");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: "Failed to download file", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return "";
    } finally {
      setIsPoTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const mergeDocRows = (prev, incoming, getKey) => {
    const existingKeys = new Set(prev.map(getKey));
    const newRows = (incoming || []).filter((row) => !existingKeys.has(getKey(row)));
    return [...prev, ...newRows];
  };

  const getServiceOrders = async (soFilter = "") => {
    try {
      setIsSoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-so`,
        params: {
          store: storeBudget?.store_name,
          id: storeBudget?.id,
          ...(soFilter ? { so_number: soFilter } : {}),
        },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      setSoData((prev) =>
        mergeDocRows(
          prev,
          res?.data || [],
          (row) =>
            `${row?.ORDER_NO || row?.order_no || ""}-${row?.SERVICE_LEDGER || row?.service_ledger || ""}-${row?.SERVICE_AMOUNT ?? row?.service_amount ?? ""}`
        )
      );
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: error?.response?.data?.message || "Failed to fetch service orders", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
    } finally {
      setIsSoTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const getSelectedServiceOrders = async () => {
    try {
      setIsSoTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-selected-so`,
        params: { id: storeBudgetData?.id },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const data = res?.data || [];
      setSavedSoRows(data);
      return [...new Set(data.map((row) => row?.order_no || row?.ORDER_NO || row?.so_number || row?.SO_NO).filter(Boolean))].join(",");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: error?.response?.data?.message || "Failed to fetch selected service orders", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return "";
    } finally {
      setIsSoTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const getApVouchers = async (
    voucherFilter = "",
    { vendorNames = [], supplierNames = [] } = {}
  ) => {
    try {
      setIsApTableLoading(true);
      setLoadingOpen(true);
      const toNameParam = (names) =>
        (Array.isArray(names) ? names : [])
          .map((name) => String(name || "").trim())
          .filter(Boolean)
          .join(",");
      const vendorNamesParam = toNameParam(vendorNames);
      const supplierNamesParam = toNameParam(supplierNames);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-ap-voucher`,
        params: {
          store: storeBudget?.store_name,
          id: storeBudget?.id,
          ...(vendorNamesParam ? { vendor_names: vendorNamesParam } : {}),
          ...(supplierNamesParam ? { supplier_names: supplierNamesParam } : {}),
          ...(voucherFilter ? { voucher_number: voucherFilter } : {}),
        },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const getApKey = (row) =>
        `${row?.VOUCHER_NO || row?.voucher_number || ""}-${row?.INSTRUMENT_NO || row?.instrument_no || ""}-${row?.AMOUNT ?? row?.amount ?? ""}`;
      const incoming = res?.data || [];
      setApData((prev) => (voucherFilter ? mergeDocRows(prev, incoming, getApKey) : incoming));
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: error?.response?.data?.message || "Failed to fetch AP vouchers", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
    } finally {
      setIsApTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const getSelectedApVouchers = async () => {
    try {
      setIsApTableLoading(true);
      setLoadingOpen(true);
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/get-selected-ap-voucher`,
        params: { id: storeBudgetData?.id },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const data = res?.data || [];
      setSavedApRows(data);
      return [...new Set(data.map((row) => row?.voucher_number || row?.VOUCHER_NO).filter(Boolean))].join(",");
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: error?.response?.data?.message || "Failed to fetch selected AP vouchers", severity: "error", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return "";
    } finally {
      setIsApTableLoading(false);
      setLoadingOpen(false);
    }
  };

  const fetchBudgetPdfs = async (budgetId = storeBudget?.id || storeBudgetData?.id) => {
    if (!budgetId) return [];
    try {
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/pdfs`,
        params: { id: budgetId },
        headers: { "Content-Type": "application/json" },
        method: "GET",
      });
      const pdfs = res?.data || [];
      setBudgetPdfs(pdfs);
      return pdfs;
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || "Failed to fetch budget PDFs",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return [];
    }
  };

  const openBudgetPdf = async (pdf, asAttachment = false) => {
    if (!pdf?.id) return;
    const previewWindow = !asAttachment ? window.open("about:blank", "_blank") : null;
    try {
      setLoadingOpen(true);
      if (!asAttachment) {
        const res = await pythonVmInstance({
          url: `/store-budget-tracker/preview-pdf`,
          params: { id: pdf.id },
          method: "GET",
        });
        const signedUrl = res?.data?.url;
        if (!signedUrl) {
          throw new Error("Failed to generate signed url");
        }
        if (previewWindow) {
          previewWindow.location.href = signedUrl;
        } else {
          window.open(signedUrl, "_blank", "noopener,noreferrer");
        }
        return;
      }
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/download-pdf`,
        params: { id: pdf.id, download: "true" },
        method: "GET",
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      FileSaver.saveAs(url, pdf?.file_name || "store_budget.pdf");
    } catch (error) {
      if (previewWindow) {
        previewWindow.close();
      }
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || error?.message || "Failed to open PDF",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  const uploadBudgetPdf = async (budgetId, file, { showLoader = true } = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("id", budgetId);
    form.append("email", user.email);
    if (showLoader) setLoadingOpen(true);
    try {
      await pythonVmInstance({
        url: `/store-budget-tracker/upload-pdf`,
        data: form,
        method: "POST",
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingPdfFile(null);
      await fetchBudgetPdfs(budgetId);
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Budget PDF uploaded successfully",
        severity: "success",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      if (showLoader) setLoadingOpen(false);
    }
  };

  const handlePdfFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Please upload a PDF file",
        severity: "warning",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return;
    }
    if (file.size > MAX_BUDGET_PDF_BYTES) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "PDF size should be 20 MB or less",
        severity: "warning",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      return;
    }

    const budgetId = storeBudget?.id || storeBudgetData?.id;
    if (!budgetId) {
      setPendingPdfFile(file);
      return;
    }

    try {
      await uploadBudgetPdf(budgetId, file);
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || "Failed to upload PDF",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    }
  };

  const handleRejectBudgetPdf = async () => {
    const activePdf = budgetPdfs.find(isActiveBudgetPdf);
    if (!activePdf?.id || !rejectPdfReason.trim()) return;
    try {
      setLoadingOpen(true);
      await pythonVmInstance({
        url: `/store-budget-tracker/reject-pdf`,
        data: JSON.stringify({
          id: activePdf.id,
          reason: rejectPdfReason.trim(),
          email: user.email,
        }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      setIsRejectPdfDialogOpen(false);
      setRejectPdfReason("");
      await fetchBudgetPdfs();
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: "Old PDF rejected. You can now upload the revised budget PDF.",
        severity: "success",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } catch (error) {
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || "Failed to reject PDF",
        severity: "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  const handleCreateStoreBudget = async (is_submitted) => {
    if (isSavingBudgetRef.current) return;
    isSavingBudgetRef.current = true;
    try {
      setLoadingOpen(true);
      const data = {
          ...storeBudget,
          store_opening_date:storeBudget?.store_opening_date || null,
          po_details: selectedPoRows.map((row) =>
            normalizePoDetailForPayload(row, getRowEvidence(row))
          ),
          so_details: serviceOrderTable.selectedDocRows.map(normalizeServiceOrderForPayload),
          ap_voucher_details: apVoucherTable.selectedDocRows.map(normalizeApVoucherForPayload),
          email: user.email,
          is_submitted: is_submitted || false,
        }
      delete data.pdfs;
      delete data.pos;
      delete data.service_orders;
      delete data.ap_vouchers;

      // 1. Save budget + PO rows (actual barcode, remarks; pictures without gcs_path are skipped)
      const res = await pythonVmInstance({
        url: `/store-budget-tracker/create`,
        data: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      });
      const createdId = res?.data?.id || storeBudget?.id;
      if (createdId) {
        setStoreBudget((prev) => ({ ...prev, id: createdId }));
      }

      // 2. Push pending item photos to GCS and persist metadata on each PO row
      if (createdId) {
        await syncPendingGrnPictures(createdId, selectedPoRows);
      }

      if (pendingPdfFile && createdId) {
        await uploadBudgetPdf(createdId, pendingPdfFile, { showLoader: false });
      }
      setLoadingOpen(false);
      setSnackBarOpen(true);
      setSnackBar({
        ...snackBar,
        message: storeBudgetData ? "Updated store budget successfully" : "created store budget successfully",
        severity: "success",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
      handleBackClick()
    } catch (error) {
      setSnackBarOpen(true);
      setLoadingOpen(false);
      setSnackBar({
        ...snackBar,
        message: error?.response?.data?.message || "error",
        anchorOrigin: { horizontal: "center", vertical: "top" },
      });
    } finally {
      isSavingBudgetRef.current = false;
    }
  };

  useEffect(() => {
    getStores();
  }, []);
  useEffect(() => {
    if (storeBudgetData?.id) {
      fetchBudgetPdfs(storeBudgetData.id);
    }
  }, []);
  useEffect(() => {
    if (isSubmitted) return;
    if (storeBudget?.store_name != "SELECT" && storeBudget?.store_name != null) {
      if (storeBudgetData?.id) {
        getSelectedPO().then((savedPoNums) => {
          getPO(savedPoNums || "");
        });
        getSelectedServiceOrders().then((savedSoNums) => {
          getServiceOrders(savedSoNums || "");
        });
      } else {
        getPO();
        getServiceOrders();
      }
    } else {
      setPoData([]);
      setSoData([]);
      setApData([]);
    }
  }, [storeBudget?.store_name]);

  const poRows = useMemo(() => {
    if (isSubmitted) {
      return savedRows.map((row, i) => ({ ...row, id: row.id || `${row.po_number}-${row.grn_no}-${row.invoice_no}-${i}` }));
    }
    return mapApiRows(poData);
  }, [poData, savedRows, isSubmitted]);

  const apVendorNames = useMemo(() => {
    const names = new Set();
    poRows.forEach((row) => {
      const cleaned = String(row?.vendor_name || "").trim();
      if (cleaned) names.add(cleaned);
    });
    return [...names];
  }, [poRows]);

  const apSupplierNames = useMemo(() => {
    const names = new Set();
    const addName = (value) => {
      const cleaned = String(value || "").trim();
      if (cleaned) names.add(cleaned);
    };
    (soData || []).forEach((row) => addName(row?.SUPPLIER_NAME ?? row?.supplier_name));
    savedSoRows.forEach((row) => addName(row?.supplier_name));
    return [...names];
  }, [soData, savedSoRows]);

  const apPartyFilterKey = [...apVendorNames, ...apSupplierNames].join("\u0001");

  useEffect(() => {
    if (isSubmitted) return;
    if (!storeBudget?.store_name || storeBudget?.store_name === "SELECT") return;

    const loadApVouchers = async () => {
      if (storeBudgetData?.id) {
        const savedVoucherNums = await getSelectedApVouchers();
        await getApVouchers(savedVoucherNums || "", {
          vendorNames: apVendorNames,
          supplierNames: apSupplierNames,
        });
        return;
      }
      await getApVouchers("", {
        vendorNames: apVendorNames,
        supplierNames: apSupplierNames,
      });
    };

    loadApVouchers();
  }, [storeBudget?.store_name, apPartyFilterKey, isSubmitted]);

  useEffect(() => {
    if (isSubmitted && storeBudgetData?.id) {
      getSelectedPO();
      getSelectedServiceOrders();
      getSelectedApVouchers();
    }
  }, []);

  useEffect(() => {
    if (!savedRows.length) return;
    setRowEvidenceMap((prev) => {
      const next = { ...prev };
      let changed = false;
      savedRows.forEach((row) => {
        if (!(row.actual_barcode || row.item_remark || row.item_comment || (row.grn_pictures && row.grn_pictures.length))) {
          return;
        }
        const key = getPoRowKey(row);
        if (prev[key]) return;
        next[key] = {
          actualBarcode: row.actual_barcode || "",
          item_remark: row.item_remark || "",
          item_comment: row.item_comment || "",
          pictures: row.grn_pictures || [],
        };
        changed = true;
      });
      return changed ? next : prev;
    });
  }, [savedRows]);

  useEffect(() => {
    const carpetArea = Number(storeBudget?.carpet_area) || 0;
    const stdPerSqFeet = Number(storeBudget?.std_per_sq_feet) || 0;
    const calculatedTotalBudget = carpetArea * stdPerSqFeet;

    setStoreBudget((prev) => {
      if ((Number(prev?.total_budget) || 0) === calculatedTotalBudget) {
        return prev;
      }

      return {
        ...prev,
        total_budget: calculatedTotalBudget,
      };
    });
  }, [storeBudget?.carpet_area, storeBudget?.std_per_sq_feet]);

  useEffect(() => {
    setStoreBudget((prev) => {
      const selectedStore = storeData?.find((store) => store.store_name === prev?.store_name);
      if (!selectedStore) return prev;

      const next = {
        ...prev,
        project: selectedStore?.project || prev.project || "",
        city: selectedStore?.city || prev.city || "",
        state: selectedStore?.state || prev.state || "",
        store_code: selectedStore?.site_code || prev.store_code || "",
      };

      if (
        next.project === prev.project &&
        next.city === prev.city &&
        next.state === prev.state &&
        next.store_code === prev.store_code
      ) {
        return prev;
      }

      return next;
    });
  }, [storeBudget?.store_name, storeBudget?.channel, storeData]);

  const selectableRowIds = useMemo(
    () => new Set(poRows.filter((row) => !isTaggedRow(row)).map((row) => row.id)),
    [poRows]
  );

  useEffect(() => {
    if (!savedRows.length || !poRows.length) return;
    if (isSubmitted) {
      const submittedIds = poRows.map((row) => row.id);
      setSelectedRows((prev) => {
        if (prev.length === submittedIds.length && prev.every((id, index) => id === submittedIds[index])) {
          return prev;
        }
        return submittedIds;
      });
      return;
    }
    const savedKeyCount = new Map();
    savedRows.forEach((r) => {
      const key = `${r?.BARCODE || r?.barcode}|${r?.PO_NO || r?.po_number}|${r?.GRN_NO || r?.grn_no}|${r?.INVOICE_NO || r?.invoice_no}`;
      savedKeyCount.set(key, (savedKeyCount.get(key) || 0) + 1);
    });
    const matchedKeyCount = new Map();
    const matchedIds = [];
    poRows.forEach((row) => {
      const key = `${row?.barcode}|${row?.po_number}|${row?.grn_no}|${row?.invoice_no}`;
      if (!savedKeyCount.has(key)) return;
      const matched = matchedKeyCount.get(key) || 0;
      if (matched < savedKeyCount.get(key)) {
        matchedIds.push(row.id);
        matchedKeyCount.set(key, matched + 1);
      }
    });
    const nextIds = matchedIds.filter((id) => selectableRowIds.has(id));
    setSelectedRows((prev) => {
      if (prev.length === nextIds.length && prev.every((id) => nextIds.includes(id))) {
        return prev;
      }
      return nextIds;
    });
  }, [poRows, savedRows, selectableRowIds, isSubmitted]);

  const selectedPoRows = useMemo(
    () => poRows.filter((row) => selectedRows.includes(row.id)),
    [poRows, selectedRows]
  );

  const filteredRows = useMemo(() => {
    const poFilters = deferredPoNumberFilter.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    const searchText = deferredTableSearch.trim().toLowerCase();
    return poRows.filter((row) => {
      const matchesPoNumber = !poFilters.length || poFilters.some((f) => row.po_number?.toLowerCase().includes(f));
      if (!matchesPoNumber) return false;
      if (!searchText) return true;

      const searchableValues = [
        row.section,
        row.material,
        row.barcode,
        row.store_name,
        row.vendor_name,
        row.po_number,
        formatDate(row.po_date),
        row.po_item_value,
        row.po_item_with_tax,
        row.po_value,
        row.po_tax,
        (Number(row.po_value) || 0) + (Number(row.po_tax) || 0),
        row.invoice_no,
        formatDate(row.invoice_date),
        row.invoice_value_without_tax,
        row.invoice_value_with_tax,
        row.grn_no,
        formatDate(row.grn_date),
        row.item_status,
        row.tagged,
        row.actual_barcode,
        row.item_remark,
        row.item_comment,
      ];

      return searchableValues.some((value) => String(value ?? "").toLowerCase().includes(searchText));
    });
  }, [deferredPoNumberFilter, poRows, deferredTableSearch]);

  const getSelectionSortRank = (row, mode, isSelected) => {
    const tagged = isTaggedRow(row);
    const selected = Boolean(isSelected);
    if (mode === "unselected") {
      if (!tagged && !selected) return 0;
      if (selected) return 1;
      return 2;
    }
    if (mode === "tagged") {
      if (tagged) return 0;
      if (selected) return 1;
      return 2;
    }
    // default / "selected" mode
    if (selected) return 0;
    if (!tagged) return 1;
    return 2;
  };

  const gridRows = useMemo(
    () =>
      filteredRows.map((row) => {
        const evidence = rowEvidenceMap[getPoRowKey(row)] || emptyRowEvidence();
        const pictureCount = evidence.pictures?.length || 0;
        const isSelected = selectedRowIdSet.has(row.id);
        return {
          ...row,
          __selected: isSelected,
          __selectionSort: getSelectionSortRank(row, selectionSortMode || "selected", isSelected),
          __hasItemDetails:
            Boolean(evidence.actualBarcode?.trim()) ||
            Boolean(evidence.item_remark?.trim()) ||
            Boolean(evidence.item_comment?.trim()) ||
            pictureCount > 0,
          __pictureCount: pictureCount,
        };
      }),
    [filteredRows, rowEvidenceMap, selectedRowIdSet, selectionSortMode]
  );

  const handleSortModelChange = (newModel) => {
    const nextSelection = newModel.find((item) => item.field === "__selectionSort");
    const prevSelection = sortModel.find((item) => item.field === "__selectionSort");
    const otherSorts = newModel.filter((item) => item.field !== "__selectionSort");

    // Sorting another column: clear selection-sort mode
    if (otherSorts.length > 0 && !nextSelection) {
      setSelectionSortMode(null);
      setSortModel(otherSorts);
      return;
    }

    const toggledSelectionColumn =
      Boolean(nextSelection) ||
      (newModel.length === 0 && (Boolean(prevSelection) || selectionSortMode != null));

    if (toggledSelectionColumn) {
      const nextMode =
        selectionSortMode == null
          ? "selected"
          : selectionSortMode === "selected"
            ? "unselected"
            : selectionSortMode === "unselected"
              ? "tagged"
              : null;
      setSelectionSortMode(nextMode);
      setSortModel(nextMode ? [{ field: "__selectionSort", sort: "asc" }] : []);
      return;
    }

    setSortModel(newModel);
  };

  const tableDisplayRows = useMemo(
    () => getRowsAfterGridFilters(filteredRows, gridFilterModel),
    [filteredRows, gridFilterModel]
  );

  const poTableSummary = useMemo(() => {
    let poAmount = 0;
    let grnAmount = 0;

    tableDisplayRows.forEach((row) => {
      poAmount += Number(row?.po_item_with_tax) || 0;
      grnAmount += Number(row?.invoice_value_with_tax) || 0;
    });

    return {
      poAmount,
      grnAmount,
      totalQty: tableDisplayRows.length,
    };
  }, [tableDisplayRows]);

  const visibleSelectableRowIds = useMemo(
    () => tableDisplayRows.filter((row) => !isTaggedRow(row)).map((row) => row.id),
    [tableDisplayRows]
  );
  visibleSelectableRowIdsRef.current = visibleSelectableRowIds;
  isBudgetAvailableRef.current = Number(storeBudget?.total_budget) > 0;

  const selectedPoNumbers = useMemo(() => Array.from(new Set(selectedPoRows.map((row) => row.po_number).filter(Boolean))), [selectedPoRows]);
  const distinctSelectedPoRows = useMemo(() => {
    const poMap = new Map();

    selectedPoRows.forEach((row) => {
      const poNumber = row?.po_number || row?.id;
      const poValue = Number(row?.po_value) || 0;
      const poTax = Number(row?.po_tax) || 0;
      const poAmountWithTax = poValue + poTax;
      const invoiceValueWithTax = Number(row?.invoice_value_with_tax) || 0;
      const normalizedGrnStatus = String(row?.item_status || "").toLowerCase();

      if (!poMap.has(poNumber)) {
        poMap.set(poNumber, {
          po_number: poNumber,
          po_value: poValue,
          po_tax: poTax,
          poAmountWithTax,
          po_status: row?.po_status || "-",
          canceled_amount: 0,
          completed_amount: 0,
        });
      }

      const existingPo = poMap.get(poNumber);

      if (normalizedGrnStatus.includes("cancel")) {
        existingPo.canceled_amount = Math.max(existingPo.canceled_amount, poAmountWithTax);
      }

      if (normalizedGrnStatus.includes("complete")) {
        existingPo.completed_amount += invoiceValueWithTax;
      }
    });

    return Array.from(poMap.values());
  }, [selectedPoRows]);
  const isBudgetAvailable = Number(storeBudget?.total_budget) > 0;

  useEffect(() => {
    if (isSubmitted) return;
    if (!isBudgetAvailable && selectedRows.length) {
      setSelectedRows([]);
    }
    if (!isBudgetAvailable && serviceOrderTable.selectedRows.length) {
      serviceOrderTable.setSelectedRows([]);
    }
    if (!isBudgetAvailable && apVoucherTable.selectedRows.length) {
      apVoucherTable.setSelectedRows([]);
    }
  }, [isBudgetAvailable, selectedRows.length, serviceOrderTable.selectedRows.length, apVoucherTable.selectedRows.length]);

  const budgetSummary = useMemo(() => {
    const totalBudget = Number(storeBudget?.total_budget) || 0;
    let poItemWithTax = 0;
    let poItemValue = 0;
    let canceledItemAmount = 0;
    let canceledItemBAmount = 0;
    let pendingItemAmount = 0;
    let pendingItemValue = 0;
    let grnValue = 0;
    let overallGrnAmount = 0;

    for (const row of selectedPoRows) {
      const status = String(row?.item_status || "").toLowerCase();
      const itemWithTax = Number(row?.po_item_with_tax) || 0;
      const itemValue = Number(row?.po_item_value) || 0;
      poItemWithTax += itemWithTax;
      poItemValue += itemValue;
      grnValue += Number(row?.invoice_value_without_tax) || 0;
      overallGrnAmount += Number(row?.invoice_value_with_tax) || 0;
      if (status.includes("cancel")) {
        canceledItemAmount += itemWithTax;
        canceledItemBAmount += itemValue;
      }
      if (status === "pending" || status === "new") {
        pendingItemAmount += itemWithTax;
        pendingItemValue += itemValue;
      }
    }

    const poTax = poItemWithTax - poItemValue;
    const poValue = poItemValue;
    const canceledAmount = distinctSelectedPoRows.reduce((sum, row) => sum + (row.canceled_amount || 0), 0);
    const completedAmount = distinctSelectedPoRows.reduce((sum, row) => sum + (row.completed_amount || 0), 0);
    const pendingAmount = distinctSelectedPoRows.reduce(
      (sum, row) => sum + Math.max((row.poAmountWithTax || 0) - (row.canceled_amount || 0) - (row.completed_amount || 0), 0),
      0
    );
    const overallPoAmount = poItemValue - canceledItemBAmount;
    const grnTax = overallGrnAmount - grnValue;
    const soAmount = serviceOrderTable.selectedDocRows.reduce(
      (sum, row) => sum + (Number(row?.service_amount) || 0),
      0
    );
    const soCount = serviceOrderTable.selectedNumbers.length;
    const apAmount = apVoucherTable.selectedDocRows.reduce(
      (sum, row) => sum + (Number(row?.amount) || 0),
      0
    );
    const apCount = apVoucherTable.selectedNumbers.length;
    const budgetDifference = totalBudget - (grnValue + pendingItemValue + soAmount);
    const utilization = totalBudget
      ? (((overallPoAmount + soAmount) / totalBudget) * 100).toFixed(2)
      : "0.00";

    return {
      totalBudget,
      poValue,
      poItemValue,
      poTax,
      overallPoAmount,
      canceledAmount,
      canceledItemAmount,
      canceledItemBAmount,
      completedAmount,
      pendingItemAmount,
      pendingAmount,
      grnValue,
      grnTax,
      overallGrnAmount,
      budgetDifference,
      pendingItemValue,
      utilization,
      soAmount,
      soCount,
      apAmount,
      apCount,
    };
  }, [
    distinctSelectedPoRows,
    selectedPoRows,
    serviceOrderTable.selectedDocRows,
    serviceOrderTable.selectedNumbers.length,
    apVoucherTable.selectedDocRows,
    apVoucherTable.selectedNumbers.length,
    storeBudget?.total_budget,
  ]);

  const handleRemovePoNumber = (poNumber) => {
    const rowIdsToRemove = new Set(
      poRows.filter((row) => row.po_number === poNumber).map((row) => row.id)
    );
    setSelectedRows((prev) => prev.filter((id) => !rowIdsToRemove.has(id)));
  };

  const validateStoreBudget = () => {
  const invalidValues = new Set(["", "SELECT", null, undefined]);

  // fields to exclude from validation
  const excludedFields = ["store_opening_date", "project_id"];

  const missingFields = storeBudgetTrackerFields
    .filter(
      (field) =>
        !excludedFields.includes(field.key) &&
        invalidValues.has(storeBudget[field.key])
    )
    .map((field) => field.label || field.key);

  return missingFields;
};

  const getGrnCompletedRowsMissingActualBarcode = () =>
    selectedPoRows.filter(
      (row) => isGrnCompleted(row) && !getRowActualBarcodeValue(row, getRowEvidence(row))
    );

  const validateItemDetails = () => {
    const missingBarcodeRows = getGrnCompletedRowsMissingActualBarcode();
    if (!missingBarcodeRows.length) return null;
    return `Actual barcode is required for ${missingBarcodeRows.length} GRN completed item(s). Open Item Details and fill actual barcode.`;
  };

  const handleSave = () => {
    const missingFields = validateStoreBudget();
    if (missingFields.length) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: `Please fill: ${missingFields.join(", ")}`, severity: "warning", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return;
    }
    handleCreateStoreBudget(false);
  };

  const handleSubmit = () => {
    const missingFields = validateStoreBudget();
    if (missingFields.length) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: `Please fill: ${missingFields.join(", ")}`, severity: "warning", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return;
    }
    const itemDetailsError = validateItemDetails();
    if (itemDetailsError) {
      setSnackBarOpen(true);
      setSnackBar({ ...snackBar, message: itemDetailsError, severity: "warning", anchorOrigin: { horizontal: "center", vertical: "top" } });
      return;
    }

    handleCreateStoreBudget(true)

  };

  const selectedStore = (() => {
    const list = (storeData || [])
      .filter((store) => store.channel === storeBudget?.channel)
      .map((st) => ({
        name: st.store_name,
        key: st.store_name,
      }))
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base", numeric: true })
      );
    const currentName = storeBudget?.store_name;
    if (currentName && currentName !== "SELECT" && !list.some((store) => store.key === currentName)) {
      list.unshift({ name: currentName, key: currentName });
    }
    return list;
  })();
  const selectedCahnnel = [...new Map(
    storeData?.map((st) => [
      st.channel,
      {
        name: st.channel,
        key: st.channel,
      },
    ])
  ).values()].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base", numeric: true })
  );

  const projectTag = storeBudget?.project || storeBudgetData?.project || storeBudgetData?.id || "-";
  const activeBudgetPdf = budgetPdfs.find(isActiveBudgetPdf);
  const canManagePdf = !isViewOnly;
  const budgetPdfRows = useMemo(
    () =>
      [...budgetPdfs]
        .sort((a, b) => (Number(b?.version) || 0) - (Number(a?.version) || 0))
        .map((pdf) => ({
          ...pdf,
          id: pdf.id,
        })),
    [budgetPdfs]
  );
  const budgetPdfColumns = useMemo(
    () => [
      {
        field: "version",
        headerName: "Ver",
        width: 70,
        type: "number",
        align: "center",
        headerAlign: "center",
      },
      {
        field: "file_name",
        headerName: "File Name",
        minWidth: 200,
        flex: 1.4,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0, width: "100%" }}>
            <PictureAsPdfOutlinedIcon sx={{ color: "#dc2626", fontSize: 18, flexShrink: 0 }} />
            <Typography variant="body2" noWrap title={row?.file_name || ""}>
              {row?.file_name || "-"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        align: "center",
        headerAlign: "center",
        valueGetter: (value, row) => (isActiveBudgetPdf(row) ? "Active" : "Rejected"),
        renderCell: ({ row }) => {
          const active = isActiveBudgetPdf(row);
          return (
            <Chip
              label={active ? "Active" : "Rejected"}
              color={active ? "success" : "default"}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: "uploaded_by",
        headerName: "Uploaded By",
        minWidth: 150,
        flex: 1,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap title={row?.uploaded_by || ""}>
            {row?.uploaded_by || "-"}
          </Typography>
        ),
      },
      {
        field: "uploaded_at",
        headerName: "Uploaded On",
        width: 130,
        renderCell: ({ row }) => formatDate(row?.uploaded_at) || "-",
      },
      {
        field: "reject_reason",
        headerName: "Reject Reason",
        minWidth: 180,
        flex: 1.2,
        renderCell: ({ row }) => (
          <Typography variant="body2" noWrap color={row?.reject_reason ? "text.primary" : "text.secondary"} title={row?.reject_reason || ""}>
            {row?.reject_reason || "-"}
          </Typography>
        ),
      },
      {
        field: "action",
        headerName: "Action",
        width: 148,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Tooltip title="Preview">
              <IconButton size="small" onClick={(event) => { event.stopPropagation(); openBudgetPdf(row); }}>
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton size="small" onClick={(event) => { event.stopPropagation(); openBudgetPdf(row, true); }}>
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canManagePdf && isActiveBudgetPdf(row) && (
              <Tooltip title="Reject PDF">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsRejectPdfDialogOpen(true);
                  }}
                >
                  <CloseOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ].map((column) => ({
      sortable: column.sortable !== false,
      headerAlign: column.headerAlign || "left",
      align: column.align || "left",
      ...column,
    })),
    [canManagePdf]
  );
  const tableColumns = useMemo(() => {
    const grnEvidenceColumn = {
      field: "grn_evidence",
      headerName: "Item Details",
      minWidth: 140,
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => {
        const completed = isGrnCompleted(row);
        const isSelected = Boolean(row.__selected);
        const pictureCount = row.__pictureCount || 0;
        const hasDetails = Boolean(row.__hasItemDetails);
        const canEdit = completed && isSelected && !isSubmitted && !isViewOnly;
        const canView = completed && hasDetails && (isSubmitted || isViewOnly || isSelected);
        const canOpen = canEdit || canView;

        if (!completed) {
          return (
            <Tooltip title="Only GRN completed rows can have item details">
              <Chip size="small" label="N/A" variant="outlined" />
            </Tooltip>
          );
        }

        if (isTaggedRow(row)) {
          if (hasDetails && (isSubmitted || isViewOnly)) {
            return (
              <Tooltip title="View item details for this row">
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={(event) => {
                      event.stopPropagation();
                      openGrnEvidenceDialogRef.current(row);
                    }}
                    startIcon={<ImageOutlinedIcon fontSize="small" />}
                  >
                    {pictureCount ? `${pictureCount} photo${pictureCount === 1 ? "" : "s"}` : "Details"}
                  </Button>
                </span>
              </Tooltip>
            );
          }

          return (
            <Tooltip title={`Tagged with project id: ${getTaggedProjectId(row, projectTag)}`} arrow placement="top">
              <Chip size="small" label="N/A" variant="outlined" />
            </Tooltip>
          );
        }

        if (!isSelected && !isSubmitted && !isViewOnly) {
          return (
            <Tooltip title="Select this row to add item details">
              <Chip size="small" label="N/A" variant="outlined" />
            </Tooltip>
          );
        }

        return (
          <Tooltip
            title={
              canEdit
                ? "Add actual barcode, photos, and remarks for this row"
                : canView
                  ? "View item details for this row"
                  : "No details added for this row"
            }
          >
            <span>
              <Button
                size="small"
                variant={hasDetails ? "contained" : "outlined"}
                color={hasDetails ? "success" : "primary"}
                disabled={!canOpen}
                onClick={(event) => {
                  event.stopPropagation();
                  openGrnEvidenceDialogRef.current(row);
                }}
                startIcon={<ImageOutlinedIcon fontSize="small" />}
              >
                {hasDetails
                  ? pictureCount
                    ? `${pictureCount} photo${pictureCount === 1 ? "" : "s"}`
                    : "Details"
                  : "Add"}
              </Button>
            </span>
          </Tooltip>
        );
      },
    };

    if (isSubmitted) {
      return [...getDataColumns(projectTag), grnEvidenceColumn];
    }

    const selectionColumn = {
      field: "__selectionSort",
      headerName: "",
      width: 80,
      sortable: true,
      filterable: false,
      disableColumnMenu: true,
      // Keep asc only; CreateStoreBudget cycles selected → unselected → tagged via sortModel
      sortingOrder: ["asc", null],
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => Number(row?.__selectionSort ?? 1),
      renderHeader: () => {
        const visibleIds = visibleSelectableRowIdsRef.current;
        const selectedIds = selectedRowIdSetRef.current;
        const checked = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
        const indeterminate = visibleIds.some((id) => selectedIds.has(id)) && !checked;
        const modeLabel =
          selectionSortMode === "selected"
            ? "Selected on top"
            : selectionSortMode === "unselected"
              ? "Unselected on top"
              : selectionSortMode === "tagged"
                ? "Tagged on top"
                : "Click sort to group by selection";

        return (
          <Tooltip title={modeLabel}>
            <Checkbox
              size="small"
              checked={checked}
              indeterminate={indeterminate}
              disabled={isSubmitted || !isBudgetAvailableRef.current || !visibleIds.length || isViewOnly}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation();
                setSelectedRows((prev) => {
                  const next = new Set(prev);
                  if (event.target.checked) {
                    visibleIds.forEach((id) => next.add(id));
                  } else {
                    visibleIds.forEach((id) => next.delete(id));
                  }
                  return Array.from(next);
                });
              }}
            />
          </Tooltip>
        );
      },
      renderCell: ({ row }) => {
        if (isTaggedRow(row)) {
          return (
            <Tooltip title={`Tagged with project id: ${getTaggedProjectId(row, projectTag)}`} arrow placement="top">
              <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "100%", color: "#2563eb" }}>
                <LocalOfferOutlinedIcon fontSize="small" />
              </Box>
            </Tooltip>
          );
        }

        const checked = Boolean(row.__selected);
        const disabled = isSubmitted || !isBudgetAvailableRef.current || isViewOnly;

        return (
          <Checkbox
            size="small"
            checked={checked}
            disabled={disabled}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              event.stopPropagation();
              setSelectedRows((prev) => {
                const next = new Set(prev);
                if (event.target.checked) {
                  next.add(row.id);
                } else {
                  next.delete(row.id);
                }
                return Array.from(next);
              });
            }}
          />
        );
      },
    };

    return [selectionColumn, ...getDataColumns(projectTag), grnEvidenceColumn];
  }, [isSubmitted, isViewOnly, projectTag, selectionSortMode]);

  const serviceOrderColumns = useMemo(() => {
    const dataColumns = getServiceOrderColumns();
    if (isSubmitted) return dataColumns;
    return [
      buildSelectionColumn({
        isSubmitted,
        isViewOnly,
        projectTag,
        selectionSortMode: serviceOrderTable.selectionSortMode,
        isBudgetAvailableRef,
        visibleSelectableRowIdsRef: serviceOrderTable.visibleSelectableRowIdsRef,
        selectedRowIdSetRef: serviceOrderTable.selectedRowIdSetRef,
        setSelectedRows: serviceOrderTable.setSelectedRows,
      }),
      ...dataColumns,
    ];
  }, [isSubmitted, isViewOnly, projectTag, serviceOrderTable.selectionSortMode, serviceOrderTable.setSelectedRows, serviceOrderTable.selectedRowIdSetRef, serviceOrderTable.visibleSelectableRowIdsRef]);

  const soTableSummaryItems = useMemo(() => {
    const rows = serviceOrderTable.gridRows || [];
    const serviceAmount = rows.reduce((sum, row) => sum + (Number(row?.service_amount) || 0), 0);
    return [
      { label: "Service Amount", value: formatCurrency(serviceAmount), minWidth: 110 },
      { label: "Total Rows", value: rows.length, minWidth: 72 },
    ];
  }, [serviceOrderTable.gridRows]);

  const apTableSummaryItems = useMemo(() => {
    const rows = apVoucherTable.gridRows || [];
    const amount = rows.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0);
    return [
      { label: "Voucher Amount", value: formatCurrency(amount), minWidth: 110 },
      { label: "Total Rows", value: rows.length, minWidth: 72 },
    ];
  }, [apVoucherTable.gridRows]);

  const apVoucherColumns = useMemo(() => {
    const dataColumns = getApVoucherColumns();
    if (isSubmitted) return dataColumns;
    return [
      buildSelectionColumn({
        isSubmitted,
        isViewOnly,
        projectTag,
        selectionSortMode: apVoucherTable.selectionSortMode,
        isBudgetAvailableRef,
        visibleSelectableRowIdsRef: apVoucherTable.visibleSelectableRowIdsRef,
        selectedRowIdSetRef: apVoucherTable.selectedRowIdSetRef,
        setSelectedRows: apVoucherTable.setSelectedRows,
      }),
      ...dataColumns,
    ];
  }, [apVoucherTable.selectionSortMode, apVoucherTable.setSelectedRows, apVoucherTable.selectedRowIdSetRef, apVoucherTable.visibleSelectableRowIdsRef, isSubmitted, isViewOnly, projectTag]);

  return (
    <Box padding={2}>
      <Stack padding={2} boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.2)"} borderRadius={2} marginTop={2}>
        <Stack direction="row" alignItems="center" marginBottom={2} flexWrap="wrap" gap={1}>
          {storeBudgetTrackerFields?.map((row) => {
            let option_data = row.option_data ? row.option_data : {};
            let alreadyCreated = false;
            if (row?.key === "channel") {
              option_data = {
                type: arrType,
                list: selectedCahnnel,
              };
            }
            if (row?.key === "store_name") {
              option_data = {
                type: arrType,
                list: selectedStore,
              };
            }
            if (row?.key === "work_type") {
              option_data = {
                ...option_data,
                list: [...(option_data?.list || [])].sort((a, b) =>
                  String(a?.name || a?.key || "").localeCompare(String(b?.name || b?.key || ""), undefined, {
                    sensitivity: "base",
                    numeric: true,
                  })
                ),
              };
            }

            if (["channel","store_name","work_type"].includes(row?.key)){
                alreadyCreated = storeBudgetData && true
            }
            const storeProject = storeData?.find((store) => store.store_name === storeBudget?.store_name)?.project;
            const savedProject = storeBudgetData?.project;
            const lockProject =
              row?.key === "project" &&
              Boolean((storeProject && storeProject !== "SELECT") || (savedProject && savedProject !== "SELECT"));
            const isAutoFilled = ["project_id", "store_code", "city", "state"].includes(row?.key) || lockProject;
            return (
              <RenderInputField
                key={row.key}
                column={{ ...row, option_data }}
                data={storeBudget}
                setData={setStoreBudget}
                width="19%"
                selectOptObjName={"option_data"}
                disabled={isSubmitted || isViewOnly || alreadyCreated || isAutoFilled}
              />
            );
          })}
        </Stack>
      </Stack>

      <Stack padding={2} boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.2)"} borderRadius={2} marginTop={3} spacing={2}>
        <input
          ref={pdfInputRef}
          hidden
          accept="application/pdf,.pdf"
          type="file"
          onChange={handlePdfFileChange}
        />
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1.5}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6">Approved Budget PDF</Typography>
              {pendingPdfFile && !activeBudgetPdf && <Chip size="small" color="warning" variant="outlined" label="Pending upload" />}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Click a row to preview. Reject the active PDF before uploading a revised file.
            </Typography>
          </Box>
          {canManagePdf && !activeBudgetPdf && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => pdfInputRef.current?.click()}
              sx={{ whiteSpace: "nowrap" }}
            >
              {pendingPdfFile ? "Change PDF" : "Upload PDF"}
            </Button>
          )}
        </Stack>

        {pendingPdfFile && !activeBudgetPdf && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: "1px dashed #93c5fd", backgroundColor: "#f8fbff" }}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
              <PictureAsPdfOutlinedIcon sx={{ color: "#dc2626", fontSize: 20 }} />
              <Typography variant="body2" noWrap title={pendingPdfFile.name}>
                {pendingPdfFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                Will upload on Save / Submit
              </Typography>
            </Stack>
            <Button size="small" color="error" onClick={() => setPendingPdfFile(null)}>
              Remove
            </Button>
          </Stack>
        )}

        <Box sx={{ width: "100%", maxHeight: 320, overflow: "hidden", border: "1px solid #e5e7eb", borderRadius: 1.5 }}>
          <DataGrid
            rows={budgetPdfRows}
            columns={budgetPdfColumns}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            onRowClick={(params) => openBudgetPdf(params.row)}
            pageSizeOptions={[5, 10]}
            initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
            localeText={{ noRowsLabel: "No PDF uploaded yet" }}
            getRowId={(row) => row.id}
            rowHeight={64}
            sx={pdfDataGridSx}
          />
        </Box>
      </Stack>

      <Dialog open={isRejectPdfDialogOpen} onClose={() => setIsRejectPdfDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject current budget PDF</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" marginBottom={2}>
            Rejecting this PDF will mark it as old. After that you can upload the revised approved budget PDF.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            required
            multiline
            minRows={3}
            label="Reject reason"
            placeholder="Enter why this budget PDF is being replaced"
            value={rejectPdfReason}
            onChange={(event) => setRejectPdfReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRejectPdfDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectPdfReason.trim()}
            onClick={handleRejectBudgetPdf}
          >
            Reject PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Stack padding={2} boxShadow={"0px 1px 3px rgba(0, 0, 0, 0.2)"} borderRadius={2} marginTop={3} spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h6">Store Budget Allocation</Typography>
            <Typography variant="body2" color="text.secondary">
              Select GRN completed rows, then use Item Details to add actual barcode, photos, and remarks.
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <TextField
              size="small"
              label="Search PO Number"
              placeholder="Enter PO Number"
              value={poNumberFilter}
              onChange={(event) => setPoNumberFilter(event.target.value)}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={!poNumberFilter.trim() || isSubmitted || isViewOnly}
              onClick={() => getPO(poNumberFilter)}
            >
              Search
            </Button>
            <TextField
              size="small"
              label="Search"
              placeholder="Search…"
              value={tableSearch}
              onChange={(event) => setTableSearch(event.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Chip label={`${selectedPoRows.length} Records`} color="primary" variant="outlined" />
            <Button variant="outlined" size="small" disabled={!selectedPoNumbers.length} onClick={() => setSelectedRows([])}>
              Clear Selection
            </Button>
          </Stack>
        </Stack>

        {!!selectedPoNumbers.length && (
          <Box sx={{ p: 1.75, borderRadius: 2, border: "1px solid #d1fae5", backgroundColor: "#f0fdf4" }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={1.5} marginBottom={1.5}>
        <Box sx={{ width: "100%"}}>
                <Typography variant="body2" color="text.secondary">Selected POs</Typography>
                <Typography variant="subtitle2" fontWeight={700}>{selectedPoNumbers.length} Selected</Typography>
              </Box>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {selectedPoNumbers.slice(0, 4).map((poNumber) => (
                <Box
                  key={poNumber}
                  sx={{
                    height: "100%",
                    minWidth: 150,
                    maxWidth: 210,
                    p: 1,
                    borderRadius: 2,
                    border: "1px solid #bbf7d0",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={400} sx={{ wordBreak: "break-word", flex: 1 }}>
                    {poNumber}
                  </Typography>
                  {(!isSubmitted && !isViewOnly) && <Box
                    onClick={() => handleRemovePoNumber(poNumber)}
                    disabled={isSubmitted || isViewOnly}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>}
                </Box>
              ))}
              {selectedPoNumbers.length > 4 && (
                <Box
                  onClick={() => setIsSelectedPoDialogOpen(true)}
                  sx={{
                    minWidth: 120,
                    p: 1,
                    borderRadius: 2,
                    border: "1px dashed #86efac",
                    backgroundColor: "#dcfce7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={400} sx={{ color: "#166534" }}>
                    +{selectedPoNumbers.length - 4} more
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            minHeight: 220,
            maxHeight: 468,
            height: Math.min(468, Math.max(220, 160 + Math.max(gridRows?.length || 0, 1) * 52)),
            border: "1px solid #e5e7eb",
            borderRadius: 1.5,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <DataGrid
              rows={gridRows}
              columns={tableColumns}
              filterModel={gridFilterModel}
              onFilterModelChange={setGridFilterModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              loading={isPoTableLoading}
              checkboxSelection={false}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              rowHeight={52}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    __check__: false,
                  },
                },
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              sx={{
                ...poDataGridSx,
                border: "none",
                height: "100%",
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: 52,
                },
              }}
            />
          </Box>

          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 1,
              borderTop: "1px solid #eceff1",
              backgroundColor: "#f8fbff",
            }}
          >
            <Box sx={{ textAlign: "left", minWidth: 110 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                PO Amount
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatCurrency(poTableSummary.poAmount)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "left", minWidth: 110 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                GRN Amount
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatCurrency(poTableSummary.grnAmount)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "left", minWidth: 72 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Rows
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {poTableSummary.totalQty}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Dialog open={isSelectedPoDialogOpen} onClose={() => setIsSelectedPoDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Selected POs</DialogTitle>
          <DialogContent dividers>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {selectedPoNumbers.map((poNumber) => (
                <Box
                  key={poNumber}
                  sx={{
                    minWidth: 150,
                    maxWidth: 220,
                    p: 1,
                    borderRadius: 2,
                    border: "1px solid #bbf7d0",
                    backgroundColor: "#f0fdf4",
                    boxShadow: "0 6px 18px rgba(22, 101, 52, 0.08)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={500} sx={{ wordBreak: "break-word", flex: 1 }}>
                    {poNumber}
                  </Typography>
                  {(!isSubmitted && !isViewOnly) && <Box
                    onClick={() => handleRemovePoNumber(poNumber)}
                    disabled={isSubmitted || isViewOnly}
                    sx={{ cursor: "pointer", color: "#dc2626", fontWeight: 700, fontSize: 16, lineHeight: 1, px: 0.5 }}
                  >
                    ×
                  </Box>}
                </Box>
              ))}
            </Stack>
          </DialogContent>
        </Dialog>

        <input
          ref={grnPictureInputRef}
          hidden
          accept={ACCEPTED_GRN_PICTURE_TYPES.join(",")}
          type="file"
          multiple
          onChange={handleGrnPictureSelect}
        />

        <Dialog open={grnEvidenceDialog.open} onClose={closeGrnEvidenceDialog} fullWidth maxWidth="sm">
          <DialogTitle>Item details</DialogTitle>
          <DialogContent dividers>
            {grnEvidenceDialog.row ? (
              <Stack spacing={2}>
                <ItemDetailsSummary row={grnEvidenceDialog.row} />

                <TextField
                  label="Actual barcode"
                  placeholder="Enter the physical barcode on the item"
                  fullWidth
                  required
                  error={actualBarcodeError}
                  helperText={actualBarcodeError ? "Actual barcode is required" : ""}
                  value={grnEvidenceDraft.actualBarcode}
                  onChange={(event) => {
                    setActualBarcodeError(false);
                    setGrnEvidenceDraft((prev) => ({ ...prev, actualBarcode: event.target.value }));
                  }}
                  disabled={isSubmitted || isViewOnly}
                />

                <TextField
                  label="Remark"
                  placeholder="Condition, quantity received, installation remarks..."
                  multiline
                  minRows={2}
                  fullWidth
                  value={grnEvidenceDraft.item_remark}
                  onChange={(event) =>
                    setGrnEvidenceDraft((prev) => ({ ...prev, item_remark: event.target.value }))
                  }
                  disabled={isSubmitted || isViewOnly}
                />

                <TextField
                  label="Comment"
                  placeholder="Any additional notes or comments..."
                  multiline
                  minRows={2}
                  fullWidth
                  value={grnEvidenceDraft.item_comment}
                  onChange={(event) =>
                    setGrnEvidenceDraft((prev) => ({ ...prev, item_comment: event.target.value }))
                  }
                  disabled={isSubmitted || isViewOnly}
                />

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={1}>
                    <Typography variant="subtitle2">Photos</Typography>
                    {!isSubmitted && !isViewOnly ? (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!grnEvidenceDraft.actualBarcode?.trim()}
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        onClick={() => {
                          if (!grnEvidenceDraft.actualBarcode?.trim()) {
                            setActualBarcodeError(true);
                            return;
                          }
                          grnPictureInputRef.current?.click();
                        }}
                      >
                        Add photos
                      </Button>
                    ) : null}
                  </Stack>

                  {grnEvidenceDraft.pictures?.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {grnEvidenceDraft.pictures.map((picture, index) => (
                        <GrnPictureThumbnail
                          key={`${picture.file_name}-${picture.gcs_path || index}`}
                          picture={picture}
                          onClick={() => openGrnPicturePreview(picture)}
                          onRemove={() => removeGrnPicture(index)}
                          showRemove={!isSubmitted && !isViewOnly}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      onClick={() => {
                        if (isSubmitted || isViewOnly) return;
                        if (!grnEvidenceDraft.actualBarcode?.trim()) {
                          setActualBarcodeError(true);
                          setSnackBarOpen(true);
                          setSnackBar({
                            ...snackBar,
                            message: "Enter actual barcode before adding photos",
                            severity: "warning",
                            anchorOrigin: { horizontal: "center", vertical: "top" },
                          });
                          return;
                        }
                        grnPictureInputRef.current?.click();
                      }}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: "1px dashed #93c5fd",
                        backgroundColor: "#f8fbff",
                        textAlign: "center",
                        cursor: isSubmitted || isViewOnly ? "default" : "pointer",
                      }}
                    >
                      <AddPhotoAlternateOutlinedIcon sx={{ color: "#2563eb", mb: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {isSubmitted || isViewOnly
                          ? "No photos added for this item"
                          : "Photos upload when you click Save details (JPG, PNG, WEBP, max 5 MB each)"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeGrnEvidenceDialog}>Close</Button>
            {!isSubmitted && !isViewOnly ? (
              <Button
                variant="contained"
                disabled={!grnEvidenceDraft.actualBarcode?.trim() || isSavingItemDetails}
                onClick={saveGrnEvidenceDraft}
              >
                {isSavingItemDetails ? "Saving..." : "Save details"}
              </Button>
            ) : null}
          </DialogActions>
        </Dialog>

        <BudgetAllocationTable
          embedded
          title="Service Order POs"
          subtitle="Search service orders, then select rows to allocate against this store budget."
          searchLabel="Search Order Number"
          searchPlaceholder="Enter Order Number"
          searchValue={soNumberFilter}
          onSearchChange={setSoNumberFilter}
          onSearch={() => getServiceOrders(soNumberFilter)}
          searchDisabled={isSubmitted || isViewOnly}
          tableSearch={soTableSearch}
          onTableSearchChange={setSoTableSearch}
          recordCount={serviceOrderTable.selectedDocRows.length}
          selectedNumbers={serviceOrderTable.selectedNumbers}
          selectedLabel="Selected Service Orders"
          moreDialogTitle="Selected Service Orders"
          onClearSelection={serviceOrderTable.handleClearSelection}
          onRemoveNumber={serviceOrderTable.handleRemoveNumber}
          isReadOnly={isSubmitted || isViewOnly}
          rows={serviceOrderTable.gridRows}
          columns={serviceOrderColumns}
          loading={isSoTableLoading}
          filterModel={serviceOrderTable.gridFilterModel}
          onFilterModelChange={serviceOrderTable.setGridFilterModel}
          sortModel={serviceOrderTable.sortModel}
          onSortModelChange={serviceOrderTable.handleSortModelChange}
          noRowsLabel="No service orders found"
          summaryItems={soTableSummaryItems}
        />

        <BudgetAllocationTable
          embedded
          title="AP Vouchers"
          subtitle="Shows vouchers where ref site matches this store and the name matches a PO vendor or Service Order supplier."
          searchLabel="Search Voucher Number"
          searchPlaceholder="Enter Voucher Number"
          searchValue={apVoucherFilter}
          onSearchChange={setApVoucherFilter}
          onSearch={() =>
            getApVouchers(apVoucherFilter, {
              vendorNames: apVendorNames,
              supplierNames: apSupplierNames,
            })
          }
          searchDisabled={isSubmitted || isViewOnly}
          tableSearch={apTableSearch}
          onTableSearchChange={setApTableSearch}
          recordCount={apVoucherTable.selectedDocRows.length}
          selectedNumbers={apVoucherTable.selectedNumbers}
          selectedLabel="Selected AP Vouchers"
          moreDialogTitle="Selected AP Vouchers"
          onClearSelection={apVoucherTable.handleClearSelection}
          onRemoveNumber={apVoucherTable.handleRemoveNumber}
          isReadOnly={isSubmitted || isViewOnly}
          rows={apVoucherTable.gridRows}
          columns={apVoucherColumns}
          loading={isApTableLoading}
          filterModel={apVoucherTable.gridFilterModel}
          onFilterModelChange={apVoucherTable.setGridFilterModel}
          sortModel={apVoucherTable.sortModel}
          onSortModelChange={apVoucherTable.handleSortModelChange}
          noRowsLabel="No AP vouchers found"
          summaryItems={apTableSummaryItems}
        />

        {(
          <Box sx={{ p: 2, borderRadius: 2, background: "linear-gradient(180deg, #f8fbff 0%, #f8fafc 100%)", border: "1px solid #e2e8f0" }}>
            <Typography variant="h6" marginBottom={0.5}>
              PO vs GRN Snapshot
            </Typography>
            <Typography variant="body2" color="text.secondary" marginBottom={2}>
              Summary of selected POs, comparing PO amounts with received GRN values to provide a quick overview of budget utilization and pending amounts.
            </Typography>

            <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
              <Stack gap={2} sx={{ flex: 1, minWidth: 320 }}>
                <Box sx={{...invoicePanelSx, height: "max-content", width: "100%"}}>
                  <Box sx={invoicePanelHeaderSx("#596fad")}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      PO Details
                    </Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.poValue)}</Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.poTax)}</Typography>
                  </Box>
                  <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#eff6ff" }}>
                    <Typography variant="subtitle2" fontWeight={700}>PO Grand Total</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary.main">
                      {formatCurrency(budgetSummary.poValue + budgetSummary.poTax)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{...invoicePanelSx, height: "max-content", width: "100%"}}>
                  <Box sx={invoicePanelHeaderSx("#0f766e")}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Service PO
                    </Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Selected Orders</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{budgetSummary.soCount}</Typography>
                  </Box>
                  <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#f0fdfa" }}>
                    <Typography variant="subtitle2" fontWeight={700}>Service Amount</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0f766e" }}>
                      {formatCurrency(budgetSummary.soAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>

              <Box sx={{...invoicePanelSx, height: "max-content"}}>
                <Box sx={invoicePanelHeaderSx("#a16207")}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    PO Level Breakdown
                  </Typography>
                </Box>
                <Box sx={{...invoiceRowSx, display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start"}}>
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography variant="body2" color="text.secondary" maxWidth={"55%"}>Pending Amount (incl tax)</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.pendingItemAmount)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" padding={0.5} borderRadius={1} backgroundColor="#dfdede">
                    <Box>
                      <Typography variant="body2" fontSize="13px" color="text.secondary">Base Amt</Typography>
                      <Typography variant="subtitle1" fontSize="12px" fontWeight={700}>{formatCurrency(budgetSummary.pendingItemValue)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontSize="13px" color="text.secondary">Tax Amt</Typography>
                      <Typography variant="subtitle1" fontSize="12px" fontWeight={700}>{formatCurrency(budgetSummary.pendingItemAmount - budgetSummary.pendingItemValue)}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{...invoiceRowSx, display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start"}}>
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography variant="body2" color="text.secondary" maxWidth={"40%"}>Canceled Amount (incl tax)</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.canceledItemAmount)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" padding={0.5} borderRadius={1} backgroundColor="#dfdede">
                    <Box>
                      <Typography variant="body2" fontSize="13px" color="text.secondary">Base Amt</Typography>
                      <Typography variant="subtitle1" fontSize="12px" fontWeight={700}>{formatCurrency(budgetSummary.canceledItemBAmount)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontSize="13px" color="text.secondary">Tax Amt</Typography>
                      <Typography variant="subtitle1" fontSize="12px" fontWeight={700}>{formatCurrency(budgetSummary.canceledItemAmount - budgetSummary.canceledItemBAmount)}</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={invoiceRowSx}>
                  <Typography variant="body2" color="text.secondary">GRN Received (incl tax)</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.completedAmount)}</Typography>
                </Box>
                <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#fef3c7" }}>
                  <Typography variant="subtitle2" fontWeight={700}>PO Item Total</Typography>
                  <Typography variant="h6" fontWeight={800} sx={{ color: "#a16207" }}>
                    {formatCurrency(budgetSummary.pendingItemAmount + budgetSummary.canceledItemAmount + budgetSummary.completedAmount)}
                  </Typography>
                </Box>
              </Box>

              <Stack gap={2} sx={{ flex: 1, minWidth: 320 }}>
                <Box sx={{...invoicePanelSx, height: "max-content", width: "100%"}}>
                  <Box sx={invoicePanelHeaderSx("#299289")}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      GRN Details
                    </Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.grnValue)}</Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Tax Amount</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(budgetSummary.grnTax)}</Typography>
                  </Box>
                  <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#ecfdf5" }}>
                    <Typography variant="subtitle2" fontWeight={700}>GRN Grand Total</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#047857" }}>
                      {formatCurrency(budgetSummary.overallGrnAmount)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{...invoicePanelSx, height: "max-content", width: "100%"}}>
                  <Box sx={invoicePanelHeaderSx("#c2410c")}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      AP Voucher
                    </Typography>
                  </Box>
                  <Box sx={invoiceRowSx}>
                    <Typography variant="body2" color="text.secondary">Selected Vouchers</Typography>
                    <Typography variant="subtitle1" fontWeight={700}>{budgetSummary.apCount}</Typography>
                  </Box>
                  <Box sx={{ ...invoiceRowSx, borderBottom: "none", backgroundColor: "#fff7ed" }}>
                    <Typography variant="subtitle2" fontWeight={700}>Voucher Amount</Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#c2410c" }}>
                      {formatCurrency(budgetSummary.apAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Stack>

            <Stack direction="row" gap={2} flexWrap="wrap" marginTop={2}>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">Total Budget</Typography>
                <Typography variant="h6">{formatCurrency(budgetSummary.totalBudget)}</Typography>
              </Box>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">
                  {budgetSummary.budgetDifference >= 0 ? "Budget Remaining" : "Budget Exceeded"}
                </Typography>
                <Typography variant="h6" color={budgetSummary.budgetDifference >= 0 ? "success.main" : "error.main"}>
                  {formatCurrency(Math.abs(budgetSummary.budgetDifference))}
                </Typography>
              </Box>
              <Box sx={comparisonCardSx}>
                <Typography variant="body2" color="text.secondary">Budget Utilization</Typography>
                <Typography variant="h6">{budgetSummary.utilization}%</Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {(!isSubmitted && !isViewOnly) && (
          <Stack direction="row" justifyContent="space-between" gap={2} flexWrap="wrap" marginTop={1}>
            <Button variant="contained" size="large" onClick={handleSubmit} disabled={roleData?.roles === "EDITOR"}>
              Submit
            </Button>
            <Button variant="outlined" size="large" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
